import React, { useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plane, Plus, Trash2, MapPin, Calendar, Loader2, Car, Download, Sparkles, Lock, Paperclip, Clock, X, FileText, Route, Gauge, Hotel, Receipt, Check, ArrowUpRight, Star, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, API, money, money2, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, PageLoader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { HotelBookings } from "@/components/travel/HotelBookings";

const PURPOSES = [
  ["wedding", "Wedding"], ["shoot", "Shoot"], ["meeting", "Meeting"], ["event", "Event"],
  ["remote_work", "Remote Work"], ["conference", "Conference / Trade Show"], ["client_visit", "Client Visit"],
  ["workshop", "Workshop / Training"], ["retreat", "Retreat"], ["site_visit", "Site / Venue Visit"],
  ["vacation", "Vacation / Personal"], ["other", "Other"],
];
const STATUSES = [["upcoming", "Upcoming"], ["active", "Active"], ["completed", "Completed"]];
const CATEGORIES = [
  ["client_visit", "Client Visit"], ["gig", "Gig"], ["scouting", "Scouting"], ["supply_run", "Supply Run"],
  ["airport", "Airport"], ["delivery", "Delivery / Drop-off"], ["site_visit", "Site / Venue Visit"],
  ["networking", "Networking Event"], ["conference", "Conference / Trade Show"], ["errand", "Business Errand"],
  ["commute", "Commute"], ["other", "Other"],
];
const label = (arr, v) => (arr.find((x) => x[0] === v) || [v, v])[1];
const purposeText = (t) => (t?.purpose === "other" && t?.purpose_other) ? t.purpose_other : label(PURPOSES, t?.purpose);
const categoryText = (l) => (l?.category === "other" && l?.category_other) ? l.category_other : label(CATEGORIES, l?.category);
const STATUS_CLS = { upcoming: "text-primary bg-primary/10", active: "text-accent bg-accent/10", completed: "text-muted-foreground bg-secondary" };

const AI_TOOLS = [
  { key: "summary", label: "Trip Summary", icon: FileText },
  { key: "packing", label: "Packing List", icon: Paperclip },
  { key: "budget", label: "Budget Estimate", icon: Gauge },
  { key: "timezone", label: "Timezone Adjustments", icon: Clock },
  { key: "optimize", label: "Optimize My Trip", icon: Route },
];

function TravelUpgradeBanner() {
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("travel_ai_nudge_dismissed") === "1");
  if (dismissed) return null;
  const close = () => { sessionStorage.setItem("travel_ai_nudge_dismissed", "1"); setDismissed(true); };
  return (
    <div className="mb-5 flex items-center gap-3 rounded-2xl border border-accent/30 bg-accent/5 p-4" data-testid="travel-ai-upgrade-banner">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent"><Sparkles className="h-4 w-4" /></div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">Unlock AI Travel with BizForge Command</p>
        <p className="text-xs text-muted-foreground">Packing lists, budget estimates, timezone tips and trip optimization — built from your real trip details.</p>
      </div>
      <Button size="sm" onClick={() => (window.location.href = "/settings")} data-testid="travel-banner-upgrade-button"><Sparkles className="mr-1.5 h-3.5 w-3.5" /> Upgrade</Button>
      <button onClick={close} className="text-muted-foreground transition-colors hover:text-foreground" aria-label="Dismiss" data-testid="travel-banner-dismiss"><X className="h-4 w-4" /></button>
    </div>
  );
}

export default function Travel() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tripParam = params.get("trip");
  const tabParam = params.get("tab");
  const [tab, setTab] = useState(tabParam || "trips");
  React.useEffect(() => { if (tabParam) setTab(tabParam); }, [tabParam]);
  const tripContext = { tid: params.get("tid") || "", dest: params.get("dest") || "", depart: params.get("depart") || "", ret: params.get("return") || "" };
  return (
    <div>
      <PageHeader title="Travel" subtitle="Plan trips and track mileage — built for freelancers on the move." testId="travel-header" />
      {user?.plan !== "business" && <TravelUpgradeBanner />}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5 h-auto max-w-full flex-wrap justify-start gap-1" data-testid="travel-tabs">
          <TabsTrigger value="trips" data-testid="tab-trips"><Plane className="mr-1.5 h-4 w-4" /> Trip Planner</TabsTrigger>
          <TabsTrigger value="mileage" data-testid="tab-mileage"><Car className="mr-1.5 h-4 w-4" /> Mileage Tracker</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings"><Hotel className="mr-1.5 h-4 w-4" /> Bookings</TabsTrigger>
        </TabsList>
        <TabsContent value="trips"><TripsTab openTripId={tripParam} onClearParam={() => setParams({}, { replace: true })} /></TabsContent>
        <TabsContent value="mileage"><MileageTab /></TabsContent>
        <TabsContent value="bookings"><HotelBookings tripContext={tripContext} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Trip Planner ---------------- */
function TripsTab({ openTripId, onClearParam }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editTrip, setEditTrip] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const { data: trips = [], isLoading } = useQuery({ queryKey: ["trips"], queryFn: async () => (await api.get("/travel/trips")).data });

  React.useEffect(() => {
    if (openTripId) setDetailId(openTripId);
  }, [openTripId]);

  const remove = async (id) => {
    if (!window.confirm("Delete this trip?")) return;
    await api.delete(`/travel/trips/${id}`);
    toast.success("Trip deleted");
    qc.invalidateQueries({ queryKey: ["trips"] });
  };

  if (isLoading) return <PageLoader />;
  return (
    <div>
      <div className="mb-5 flex justify-end"><Button onClick={() => { setEditTrip(null); setOpen(true); }} data-testid="new-trip-button"><Plus className="mr-2 h-4 w-4" /> New trip</Button></div>
      {trips.length === 0 ? (
        <EmptyState icon={Plane} title="No trips yet" description="Plan your next shoot, wedding, or client trip and keep the itinerary in one place." action={<Button onClick={() => setOpen(true)}><Plus className="mr-2 h-4 w-4" /> New trip</Button>} testId="trips-empty" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {trips.map((t) => (
            <div key={t.id} className="flex flex-col rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:shadow-md" data-testid={`trip-card-${t.id}`}>
              <div className="flex items-start justify-between">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${STATUS_CLS[t.status]}`}>{label(STATUSES, t.status)}</span>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(t.id)} data-testid={`delete-trip-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <h3 className="mt-2 flex items-center gap-1.5 font-display text-base font-semibold"><MapPin className="h-4 w-4 text-accent" /> {t.destination}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{purposeText(t)}</p>
              {t.start_date && <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><Calendar className="h-3.5 w-3.5" /> {t.start_date}{t.end_date ? ` → ${t.end_date}` : ""}</p>}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(t.tags || []).map((tag) => <Badge key={tag} variant="secondary" className="text-[10px]">{tag}</Badge>)}
              </div>
              <div className="mt-4 flex items-center gap-2 border-t border-border pt-4">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => setDetailId(t.id)} data-testid={`open-trip-${t.id}`}>Open</Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditTrip(t); setOpen(true); }} data-testid={`edit-trip-${t.id}`}>Edit</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <TripDialog open={open} onOpenChange={setOpen} trip={editTrip} />
      {detailId && <TripDetail tripId={detailId} onClose={() => { setDetailId(null); if (onClearParam) onClearParam(); }} />}
    </div>
  );
}

function TripDialog({ open, onOpenChange, trip }) {
  const qc = useQueryClient();
  const EMPTY = { destination: "", start_date: "", end_date: "", purpose: "meeting", purpose_other: "", notes: "", status: "upcoming", tags: "" };
  const [form, setForm] = useState(EMPTY);
  React.useEffect(() => {
    if (open) setForm(trip ? { ...trip, purpose_other: trip.purpose_other || "", tags: (trip.tags || []).join(", ") } : EMPTY);
  }, [open, trip]);

  const save = async () => {
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : [] };
      if (trip) await api.patch(`/travel/trips/${trip.id}`, payload);
      else await api.post("/travel/trips", payload);
      toast.success(trip ? "Trip updated" : "Trip created");
      onOpenChange(false);
      qc.invalidateQueries({ queryKey: ["trips"] });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="trip-dialog">
        <DialogHeader><DialogTitle>{trip ? "Edit trip" : "New trip"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Destination</Label><Input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder="e.g. Austin, TX" data-testid="trip-destination-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Start date</Label><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="trip-start-input" /></div>
            <div className="space-y-1.5"><Label>End date</Label><Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="trip-end-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Purpose</Label>
              <Select value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}>
                <SelectTrigger data-testid="trip-purpose-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PURPOSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="trip-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {form.purpose === "other" && (
            <div className="space-y-1.5"><Label>Describe purpose</Label><Input value={form.purpose_other} onChange={(e) => setForm({ ...form, purpose_other: e.target.value })} placeholder="e.g. Portfolio scouting" data-testid="trip-purpose-other-input" /></div>
          )}
          <div className="space-y-1.5"><Label>Tags <span className="font-normal text-muted-foreground">(comma separated)</span></Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="wedding, priority" data-testid="trip-tags-input" /></div>
          <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="trip-notes-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!form.destination} data-testid="save-trip-button">{trip ? "Save changes" : "Create trip"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TripCostCard({ tripId, staysCount }) {
  const { data: c } = useQuery({ queryKey: ["trip-costs", tripId, staysCount], queryFn: async () => (await api.get(`/travel/trips/${tripId}/costs`)).data });
  if (!c) return null;
  const rows = [
    { key: "hotels", label: "Hotels", value: c.hotels, sub: c.counts.stays ? `${c.counts.stays} saved stay${c.counts.stays > 1 ? "s" : ""}` : "No stays saved", color: "bg-accent" },
    { key: "mileage", label: "Mileage", value: c.mileage, sub: c.counts.drives ? `${c.miles} mi · ${c.counts.drives} drive${c.counts.drives > 1 ? "s" : ""}${c.billed_mileage ? ` · ${money2(c.billed_mileage)} billed` : ""}` : "No drives logged", color: "bg-sky-400" },
    { key: "expenses", label: "Expenses", value: c.expenses, sub: c.counts.expenses ? `${c.counts.expenses} item${c.counts.expenses > 1 ? "s" : ""}` : "No expenses logged", color: "bg-amber-400" },
  ];
  return (
    <div className="rounded-2xl bg-secondary/40 p-4 ring-1 ring-border/40" data-testid="trip-cost-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Wallet className="h-3.5 w-3.5 text-accent" /> Trip cost so far</p>
          <p className="mt-1 font-display text-2xl font-bold" data-testid="trip-cost-total">{money2(c.total)}</p>
        </div>
        {!c.has_dates && <p className="max-w-[180px] text-right text-[11px] text-muted-foreground">Add trip dates to include mileage and expenses logged during the trip.</p>}
      </div>
      {c.total > 0 && (
        <div className="mt-3 flex h-2 overflow-hidden rounded-full bg-secondary" data-testid="trip-cost-bar">
          {rows.filter((r) => r.value > 0).map((r) => <div key={r.key} className={`${r.color} transition-all duration-500`} style={{ width: `${(r.value / c.total) * 100}%` }} />)}
        </div>
      )}
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {rows.map((r) => (
          <div key={r.key} className="rounded-xl bg-card/70 px-3 py-2" data-testid={`trip-cost-${r.key}`}>
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground"><span className={`h-1.5 w-1.5 rounded-full ${r.color}`} /> {r.label}</p>
            <p className="text-sm font-semibold">{money2(r.value)}</p>
            <p className="truncate text-[11px] text-muted-foreground">{r.sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function TripDetail({ tripId, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef();
  const isCommand = user?.plan === "business";
  const [itin, setItin] = useState({ time: "", description: "" });
  const [aiBusy, setAiBusy] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const { data: trip, isLoading } = useQuery({ queryKey: ["trips", tripId], queryFn: async () => (await api.get(`/travel/trips/${tripId}`)).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["trips", tripId] }); qc.invalidateQueries({ queryKey: ["trips"] }); };

  const addItin = async () => {
    if (!itin.description) return;
    await api.post(`/travel/trips/${tripId}/itinerary`, itin);
    setItin({ time: "", description: "" });
    refresh();
  };
  const delItin = async (id) => { await api.delete(`/travel/trips/${tripId}/itinerary/${id}`); refresh(); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("Attachment exceeds 25 MB."); return; }
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/travel/trips/${tripId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attached");
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const viewAttach = async (fid) => {
    const tab = window.open("", "_blank", "noopener");
    try {
      const res = await api.get(`/travel/attachments/${tripId}/${fid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (tab) tab.location = url;
    } catch { if (tab) tab.close(); toast.error("Couldn't open attachment."); }
  };
  const delAttach = async (fid) => { await api.delete(`/travel/trips/${tripId}/attachments/${fid}`); refresh(); };
  const delStay = async (sid) => { await api.delete(`/travel/trips/${tripId}/stays/${sid}`); toast.success("Stay removed"); refresh(); };

  const bookTravel = () => {
    onClose();
    const q = new URLSearchParams({ tab: "bookings", tid: trip?.id || "", dest: trip?.destination || "", depart: trip?.start_date || "", return: trip?.end_date || "" });
    navigate(`/travel?${q.toString()}`);
  };

  const runAI = async (key) => {
    setAiBusy(key); setAiResult(null);
    try {
      const res = await api.post(`/travel/trips/${tripId}/ai/${key}`);
      setAiResult({ key, text: res.data.result });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setAiBusy(""); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="trip-detail-dialog">
        {isLoading || !trip ? <div className="flex justify-center py-10"><DialogTitle className="sr-only">Loading trip</DialogTitle><Loader2 className="h-6 w-6 animate-spin text-accent" /></div> : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-accent" /> {trip.destination}</DialogTitle>
              <DialogDescription>{purposeText(trip)} · {trip.start_date || "TBD"}{trip.end_date ? ` → ${trip.end_date}` : ""} · {label(STATUSES, trip.status)}</DialogDescription>
            </DialogHeader>
            <Button variant="outline" className="w-full border-accent/40 text-accent hover:bg-accent/10 hover:text-accent" onClick={bookTravel} data-testid="book-travel-button"><Hotel className="mr-2 h-4 w-4" /> Book a hotel for this trip</Button>
            <div className="max-h-[70vh] space-y-5 overflow-y-auto pr-1">
              {trip.notes && <p className="rounded-xl bg-secondary/50 p-3 text-sm text-muted-foreground">{trip.notes}</p>}
              <TripCostCard tripId={tripId} staysCount={(trip.stays || []).length} />

              {/* Itinerary */}
              <div>
                <p className="mb-2 text-sm font-semibold">Itinerary</p>
                <div className="space-y-2">
                  {(trip.itinerary || []).map((i) => (
                    <div key={i.id} className="flex items-center gap-3 rounded-lg border border-border p-2.5 text-sm" data-testid={`itinerary-${i.id}`}>
                      <span className="w-14 shrink-0 font-medium text-accent">{i.time || "—"}</span>
                      <span className="flex-1">{i.description}</span>
                      <button onClick={() => delItin(i.id)} className="text-muted-foreground hover:text-destructive" data-testid={`del-itin-${i.id}`}><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                  {(trip.itinerary || []).length === 0 && <p className="text-xs text-muted-foreground">No itinerary items yet.</p>}
                </div>
                <div className="mt-2 flex gap-2">
                  <Input className="w-24" placeholder="09:00" value={itin.time} onChange={(e) => setItin({ ...itin, time: e.target.value })} data-testid="itin-time-input" />
                  <Input className="flex-1" placeholder="Add itinerary item" value={itin.description} onChange={(e) => setItin({ ...itin, description: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addItin()} data-testid="itin-desc-input" />
                  <Button size="sm" onClick={addItin} data-testid="add-itin-button"><Plus className="h-4 w-4" /></Button>
                </div>
              </div>

              {/* Saved stays */}
              {(trip.stays || []).length > 0 && (
                <div data-testid="trip-stays">
                  <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold"><Hotel className="h-4 w-4 text-accent" /> Saved stays</p>
                  <div className="space-y-2">
                    {trip.stays.map((st) => (
                      <div key={st.id} className="flex items-center gap-3 overflow-hidden rounded-2xl bg-secondary/40 p-2 pr-3 ring-1 ring-border/40" data-testid={`trip-stay-${st.id}`}>
                        {st.image ? <img src={st.image} alt="" className="h-14 w-20 shrink-0 rounded-xl object-cover" /> : <div className="flex h-14 w-20 shrink-0 items-center justify-center rounded-xl bg-secondary"><Hotel className="h-5 w-5 text-muted-foreground" /></div>}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{st.name}</p>
                          <p className="text-xs text-muted-foreground">{st.check_in} → {st.check_out}{st.star_rating ? <span className="ml-1.5 inline-flex items-center gap-0.5 text-amber-400"><Star className="h-3 w-3 fill-current" />{st.star_rating}</span> : null}</p>
                          <p className="text-xs"><span className="font-semibold">{money2(st.price_per_night)}</span><span className="text-muted-foreground">/night · {money2(st.total_price)} total</span></p>
                        </div>
                        {st.book_url && <a href={st.book_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2.5 py-1 text-xs font-semibold text-accent transition-colors hover:bg-accent hover:text-accent-foreground" data-testid={`trip-stay-book-${st.id}`}>Book <ArrowUpRight className="h-3 w-3" /></a>}
                        <button onClick={() => delStay(st.id)} className="text-muted-foreground hover:text-destructive" data-testid={`del-stay-${st.id}`}><X className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Attachments */}
              <div>
                <p className="mb-1 text-sm font-semibold">Attachments &amp; booking confirmations</p>
                <p className="mb-2 text-xs text-muted-foreground">Save flight/hotel confirmations, contracts or itineraries to this trip.</p>
                <div className="space-y-2">
                  {(trip.attachments || []).map((a) => (
                    <div key={a.id} className="flex items-center gap-3 rounded-lg border border-border p-2.5 text-sm" data-testid={`attach-${a.id}`}>
                      <Paperclip className="h-4 w-4 text-muted-foreground" />
                      <button onClick={() => viewAttach(a.id)} className="flex-1 truncate text-left hover:text-accent" data-testid={`view-attach-${a.id}`}>{a.name}</button>
                      <button onClick={() => delAttach(a.id)} className="text-muted-foreground hover:text-destructive" data-testid={`del-attach-${a.id}`}><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
                <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="attach-input" />
                <Button size="sm" variant="outline" className="mt-2" onClick={() => fileRef.current?.click()} data-testid="attach-button"><Paperclip className="mr-1.5 h-3.5 w-3.5" /> Attach confirmation or file (max 25 MB)</Button>
              </div>

              {/* AI tools (Command) */}
              <div className="rounded-xl border border-border p-4">
                <div className="mb-3 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-accent" />
                  <p className="text-sm font-semibold">AI Travel Assistant</p>
                  <Badge variant="outline" className="gap-1 border-accent/40 text-accent"><Lock className="h-3 w-3" /> Command</Badge>
                </div>
                {!isCommand ? (
                  <div className="rounded-lg bg-secondary/50 p-4 text-center" data-testid="travel-ai-locked">
                    <p className="text-sm text-muted-foreground">Trip Summary, Packing List, Budget Estimate, Timezone Adjustments, and Optimize My Trip are part of <span className="font-semibold text-foreground">BizForge Command</span>.</p>
                    <Button size="sm" className="mt-3" onClick={() => (window.location.href = "/settings")} data-testid="travel-ai-upgrade"><Sparkles className="mr-1.5 h-3.5 w-3.5" /> Upgrade to unlock</Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {AI_TOOLS.map((t) => (
                      <Button key={t.key} size="sm" variant="outline" disabled={!!aiBusy} onClick={() => runAI(t.key)} data-testid={`ai-${t.key}-button`}>
                        {aiBusy === t.key ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <t.icon className="mr-1.5 h-3.5 w-3.5" />} {t.label}
                      </Button>
                    ))}
                  </div>
                )}
                {aiResult && (
                  <div className="mt-3 whitespace-pre-wrap rounded-lg bg-secondary/40 p-3 text-sm leading-relaxed" data-testid="travel-ai-result">{aiResult.text}</div>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Mileage Tracker ---------------- */
function MileageTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [billLog, setBillLog] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["mileage"], queryFn: async () => (await api.get("/travel/mileage")).data });

  const remove = async (id) => { await api.delete(`/travel/mileage/${id}`); qc.invalidateQueries({ queryKey: ["mileage"] }); };
  const exportCsv = async () => {
    try {
      const res = await api.get("/travel/mileage/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "mileage_log.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  if (isLoading) return <PageLoader />;
  const logs = data?.logs || [];
  return (
    <div>
      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-4"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Total miles</p><p className="mt-1 font-display text-2xl font-bold">{data?.total_miles ?? 0}</p></div>
        <div className="rounded-2xl border border-border bg-card p-4"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reimbursement</p><p className="mt-1 font-display text-2xl font-bold text-accent">{money2(data?.total_reimbursement || 0)}</p></div>
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={exportCsv} disabled={logs.length === 0} data-testid="export-mileage-button"><Download className="mr-2 h-4 w-4" /> Export CSV</Button>
          <Button onClick={() => setOpen(true)} data-testid="add-mileage-button"><Plus className="mr-2 h-4 w-4" /> Log trip</Button>
        </div>
      </div>
      {logs.length === 0 ? (
        <EmptyState icon={Car} title="No mileage logged" description="Log a drive and BizForge calculates your reimbursement automatically." action={<Button onClick={() => setOpen(true)}><Plus className="mr-2 h-4 w-4" /> Log trip</Button>} testId="mileage-empty" />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Route</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Miles</th><th className="px-4 py-3">Rate</th><th className="px-4 py-3">Total</th><th className="px-4 py-3 text-right">Billing</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {logs.map((l) => (
                <tr key={l.id} className="bg-card" data-testid={`mileage-row-${l.id}`}>
                  <td className="px-4 py-3 text-muted-foreground">{l.date}</td>
                  <td className="px-4 py-3"><span className="font-medium">{l.start_location || "—"}</span> → {l.end_location || "—"}{l.purpose ? <span className="block text-xs text-muted-foreground">{l.purpose}</span> : null}</td>
                  <td className="px-4 py-3"><Badge variant="secondary">{categoryText(l)}</Badge></td>
                  <td className="px-4 py-3">{l.miles}</td>
                  <td className="px-4 py-3 text-muted-foreground">{money2(l.rate)}</td>
                  <td className="px-4 py-3 font-semibold">{money2(l.total)}</td>
                  <td className="px-4 py-3 text-right">
                    {l.billed ? (
                      <Badge variant="outline" className="gap-1 border-accent/40 text-accent" data-testid={`billed-badge-${l.id}`}><Check className="h-3 w-3" /> {l.invoice_number}</Badge>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setBillLog(l)} data-testid={`bill-mileage-${l.id}`}><Receipt className="mr-1.5 h-3.5 w-3.5" /> Bill to client</Button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right"><Button size="icon" variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={() => remove(l.id)} data-testid={`delete-mileage-${l.id}`}><Trash2 className="h-4 w-4" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <MileageDialog open={open} onOpenChange={setOpen} defaultRate={data?.default_rate || 0.7} />
      {billLog && <BillDialog log={billLog} onClose={() => setBillLog(null)} />}
    </div>
  );
}

function BillDialog({ log, onClose }) {
  const qc = useQueryClient();
  const { data: contacts = [] } = useQuery({ queryKey: ["contacts"], queryFn: async () => (await api.get("/contacts")).data });
  const [client, setClient] = useState({ name: "", email: "" });
  const [saving, setSaving] = useState(false);

  const pickContact = (id) => {
    const c = contacts.find((x) => x.id === id);
    setClient({ name: c?.name || "", email: c?.email || "" });
  };
  const submit = async () => {
    if (!client.name.trim()) { toast.error("Choose or enter a client name."); return; }
    setSaving(true);
    try {
      const res = await api.post(`/travel/mileage/${log.id}/bill`, { client_name: client.name.trim(), client_email: client.email });
      toast.success(`Draft invoice ${res.data.invoice_number} created for ${client.name}`);
      qc.invalidateQueries({ queryKey: ["mileage"] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const total = (log.miles * log.rate).toFixed(2);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="bill-mileage-dialog">
        <DialogHeader>
          <DialogTitle>Bill mileage to a client</DialogTitle>
          <DialogDescription>Creates a draft invoice with this drive as a line item. Review and send it from Invoicing &amp; Expenses.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-xl border border-border bg-secondary/40 p-3 text-sm">
            <p className="font-medium">{log.start_location || "Start"} → {log.end_location || "End"}</p>
            <p className="text-muted-foreground">{log.miles} mi @ {money2(log.rate)}/mi · <span className="font-semibold text-accent">{money2(Number(total))}</span></p>
          </div>
          {contacts.length > 0 && (
            <div className="space-y-1.5"><Label>Choose a client</Label>
              <Select onValueChange={pickContact}>
                <SelectTrigger data-testid="bill-client-select"><SelectValue placeholder="Select from Clients & Prospects" /></SelectTrigger>
                <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5"><Label>Client name</Label><Input value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} placeholder="Client or company name" data-testid="bill-client-name-input" /></div>
          <div className="space-y-1.5"><Label>Client email <span className="font-normal text-muted-foreground">(optional)</span></Label><Input value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} placeholder="billing@client.com" data-testid="bill-client-email-input" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={saving || !client.name.trim()} data-testid="confirm-bill-button">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Receipt className="mr-2 h-4 w-4" />} Create draft invoice</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MileageDialog({ open, onOpenChange, defaultRate }) {
  const qc = useQueryClient();
  const EMPTY = { date: "", start_location: "", end_location: "", purpose: "", miles: "", rate: defaultRate, category: "client_visit", category_other: "" };
  const [form, setForm] = useState(EMPTY);
  const [calc, setCalc] = useState(false);
  React.useEffect(() => { if (open) setForm({ ...EMPTY, rate: defaultRate }); }, [open, defaultRate]);

  const calcDistance = async () => {
    if (!form.start_location || !form.end_location) { toast.error("Enter both start and end locations."); return; }
    setCalc(true);
    try {
      const res = await api.get(`/travel/mileage/distance`, { params: { start: form.start_location, end: form.end_location } });
      setForm((f) => ({ ...f, miles: res.data.miles }));
      toast.success(`Distance calculated: ${res.data.miles} mi`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setCalc(false); }
  };

  const save = async () => {
    try {
      await api.post("/travel/mileage", { ...form, miles: Number(form.miles) || 0, rate: Number(form.rate) || 0 });
      toast.success("Trip logged");
      onOpenChange(false);
      qc.invalidateQueries({ queryKey: ["mileage"] });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const total = ((Number(form.miles) || 0) * (Number(form.rate) || 0)).toFixed(2);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mileage-dialog">
        <DialogHeader><DialogTitle>Log a drive</DialogTitle><DialogDescription>Enter your route, then calculate distance or type the miles.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="mileage-date-input" /></div>
            <div className="space-y-1.5"><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="mileage-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {form.category === "other" && (
            <div className="space-y-1.5"><Label>Describe category</Label><Input value={form.category_other} onChange={(e) => setForm({ ...form, category_other: e.target.value })} placeholder="e.g. Equipment pickup" data-testid="mileage-category-other-input" /></div>
          )}
          <div className="space-y-1.5"><Label>Start location</Label><Input value={form.start_location} onChange={(e) => setForm({ ...form, start_location: e.target.value })} placeholder="e.g. Austin, TX" data-testid="mileage-start-input" /></div>
          <div className="space-y-1.5"><Label>End location</Label><Input value={form.end_location} onChange={(e) => setForm({ ...form, end_location: e.target.value })} placeholder="e.g. Dallas, TX" data-testid="mileage-end-input" /></div>
          <Button variant="outline" size="sm" onClick={calcDistance} disabled={calc} data-testid="calc-distance-button">{calc ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Route className="mr-1.5 h-3.5 w-3.5" />} Calculate driving distance</Button>
          <div className="space-y-1.5"><Label>Purpose</Label><Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="e.g. Client visit" data-testid="mileage-purpose-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Miles</Label><Input type="number" value={form.miles} onChange={(e) => setForm({ ...form, miles: e.target.value })} data-testid="mileage-miles-input" /></div>
            <div className="space-y-1.5"><Label>Rate ($/mi)</Label><Input type="number" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} data-testid="mileage-rate-input" /></div>
          </div>
          <div className="rounded-lg bg-accent/10 p-3 text-sm font-medium text-accent" data-testid="mileage-total-preview">Total reimbursement: ${total}</div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="save-mileage-button">Save log</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
